import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ClipboardCheck, Package, Search, Wrench,
  CheckCircle, AlertTriangle, FileText, ArrowRight
} from 'lucide-react';

const AuditDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/audit-portal/equipment');
      setEquipment(res.data);
    } catch (err) {
      toast.error('Eroare la incarcarea echipamentelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  const filtered = equipment.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.name || '').toLowerCase().includes(s) ||
           (e.code || '').toLowerCase().includes(s) ||
           (e.location_name || '').toLowerCase().includes(s);
  });

  const totalRevisions = equipment.reduce((sum, e) => sum + (e.revision_count || 0), 0);
  const totalTickets = equipment.reduce((sum, e) => sum + (e.ticket_count || 0), 0);

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
          Portal Audit
        </h1>
        <p className="text-slate-600 mt-1">
          Bine ai venit, {user?.full_name}. Selecteaza un echipament pentru detalii.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 lg:gap-6 mb-6">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{equipment.length}</p>
                <p className="text-xs text-slate-500">Echipamente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalRevisions}</p>
                <p className="text-xs text-slate-500">Revizii totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalTickets}</p>
                <p className="text-xs text-slate-500">Tichete totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Cauta echipament (cod, nume, locatie)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 text-base"
          data-testid="audit-search"
        />
      </div>

      {/* Quick scan button for mobile */}
      <div className="lg:hidden mb-4">
        <Button
          className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base"
          onClick={() => navigate('/audit/scan')}
          data-testid="quick-scan-btn"
        >
          <ClipboardCheck className="w-5 h-5 mr-2" />
          Scaneaza QR Echipament
        </Button>
      </div>

      {/* Equipment List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(eq => (
            <Card
              key={eq.id}
              className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/audit/equipment/${eq.code || eq.id}`)}
              data-testid={`equipment-card-${eq.code}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{eq.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="font-mono">{eq.code}</span>
                        {eq.location_name && (
                          <>
                            <span>-</span>
                            <span className="truncate">{eq.location_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <Badge variant="outline" className="hidden sm:flex text-xs">
                      <FileText className="w-3 h-3 mr-1" />
                      {eq.revision_count || 0} revizii
                    </Badge>
                    <Badge variant={eq.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                      {eq.status === 'active' ? 'Activ' : eq.status || 'N/A'}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Niciun echipament gasit</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditDashboardPage;
